import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useKeycloak } from '@hooks/useKeycloak';
import { ROUTE_PATHS } from '@router/paths';

interface AuthData {
  isLoggedIn: boolean;
  onLogin: (redirectUrl?: string) => void;
  onLogout: () => void;
  onInvalidAuth: () => void;
  onSessionExpired: () => void;
}

const useAuth = (): AuthData => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const accessToken = keycloak?.token;
  const isLoggedIn = useMemo(() => !!keycloak?.authenticated && !!accessToken, [keycloak?.authenticated, accessToken]);

  const onLogout = () => {
    // clearUser() handled by onAuthLogout in useKeycloak
    if (keycloak) {
      keycloak!.logout({ redirectUri: window.location.origin + ROUTE_PATHS.LOGIN });
    } else {
      navigate(ROUTE_PATHS.LOGIN);
    }
  };

  const onLogin = (redirectUrl: string = ROUTE_PATHS.REQUISITION_LIST) => {
    keycloak?.login({ redirectUri: window.location.origin + redirectUrl });
  };

  const onInvalidAuth = useCallback(() => {
    if (keycloak) {
      keycloak.logout({ redirectUri: window.location.href });
    } else {
      navigate(ROUTE_PATHS.LOGIN);
    }
  }, [keycloak, navigate]);

  const onSessionExpired = useCallback(() => {
    navigate(ROUTE_PATHS.SESSION_EXPIRED);
  }, [navigate]);

  return {
    isLoggedIn,
    onLogin,
    onLogout,
    onInvalidAuth,
    onSessionExpired,
  };
};

export default useAuth;
